import { useEffect, useState, useMemo, useRef } from "react";
import {
  X, ArrowLeft, FileText, ChefHat, Info, TrendingUp, TrendingDown, Minus,
  Package, Building2, Barcode, Tag, Loader2, Eye, Sparkles, Plus, Trash2, GitMerge, ChevronRight,
  Pencil, Check, XCircle, LineChart as LineChartIcon, BarChart3, Lock, AlertTriangle,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip as RcTooltip, ResponsiveContainer,
  CartesianGrid, ReferenceDot, Cell,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import DocumentViewer from "./DocumentViewer";
import RecipeEditorDialog from "./RecipeEditorDialog";
import { CategoryIcon } from "./CategoryIcon";
import ProductPriceChart from "./ProductPriceChart";
import InvoiceLineEditDialog from "./InvoiceLineEditDialog";
import { InvoiceAnalysis } from "../pages/Invoices";
import { comparableHistory } from "../lib/priceHistory";
import { api } from "../lib/api";
import { eur, dateFR, pct } from "../lib/format";
import { UNITS, unitLabel } from "../lib/format";
import SafeImage from "./SafeImage";

// Chaque période porte son propre plafond de plan minimal. Les périodes >
// 6 mois sont réservées aux plans Pro / Intégral (voir `plans.py`).
const PERIODS = [
  { key: "30d", label: "30 jours",        days: 30,   minPlan: "essentiel" },
  { key: "3m",  label: "3 mois",          days: 92,   minPlan: "essentiel" },
  { key: "6m",  label: "6 mois",          days: 183,  minPlan: "essentiel" },
  { key: "1y",  label: "12 mois",         days: 365,  minPlan: "pro" },
  { key: "all", label: "Toute la période", days: null, minPlan: "integral" },
];

const PLAN_RANK = { essentiel: 0, pro: 1, integral: 2 };
const planAllows = (userPlan, minPlan) => (PLAN_RANK[userPlan] ?? 0) >= (PLAN_RANK[minPlan] ?? 0);

export default function ProductDetail({ productId, onClose, onChanged }) {
  // Feb 2026 — Ouverture d'une recette liée en overlay superposé (pas de
  // navigation vers /plats). Le state est local à la fiche produit : à la
  // fermeture, l'utilisateur retrouve exactement la fiche produit et sa
  // page d'origine (Produits, Aujourd'hui, Tableau de bord, …) intactes.
  const [overlayRecipeId, setOverlayRecipeId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [period, setPeriod] = useState("3m");
  // Type de visualisation persistant : "line" (courbe) ou "bar" (barres).
  const [chartMode, setChartMode] = useState(() => {
    if (typeof window === "undefined") return "line";
    return window.localStorage.getItem("rentascan:chart-mode") || "line";
  });
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("rentascan:chart-mode", chartMode);
  }, [chartMode]);
  // Plan effectif de l'utilisateur — utilisé pour verrouiller les périodes
  // > 6 mois aux abonnements Pro / Intégral.
  const [userPlan, setUserPlan] = useState("pro");
  useEffect(() => {
    let cancelled = false;
    api.planMe().then((p) => { if (!cancelled && p?.effective_plan) setUserPlan(p.effective_plan); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [invoicePreview, setInvoicePreview] = useState(null); // full invoice with file_url
  const [aliases, setAliases] = useState([]);
  const [newAlias, setNewAlias] = useState({ raw_text: "", supplier_name: "" });
  const [allProducts, setAllProducts] = useState([]);
  const [mergeInto, setMergeInto] = useState("");
  const [suppliersList, setSuppliersList] = useState([]);
  const [editIdentity, setEditIdentity] = useState(false);
  const [identityDraft, setIdentityDraft] = useState(null);
  const [savingIdentity, setSavingIdentity] = useState(false);
  // Feb 2026 §« clic point courbe → édition ligne source »
  // {invoice, lineIndex, line} — rendu par InvoiceLineEditDialog en overlay.
  const [chartEdit, setChartEdit] = useState(null);
  // Bumps whenever we want to replay the chart entry animation (open, filter change).
  const [chartKey, setChartKey] = useState(0);
  // Users with prefers-reduced-motion get an instant chart.
  const reducedMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const reloadAliases = () => api.productAliases(productId).then(setAliases).catch(() => {});
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.productDetail(productId), api.productAliases(productId), api.products(), api.suppliers()])
      .then(([d, a, ps, sp]) => {
        if (cancelled) return;
        setData(d); setAliases(a); setAllProducts(ps); setSuppliersList(sp || []); setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId]);

  const suppliers = useMemo(() => {
    if (!data) return [];
    const set = new Set((data.history || []).map((h) => h.supplier_name).filter(Boolean));
    return Array.from(set);
  }, [data]);

  const filteredHistory = useMemo(() => {
    if (!data) return [];
    let list = comparableHistory(data.history || []);
    if (supplierFilter !== "all") list = list.filter((h) => h.supplier_name === supplierFilter);
    if (period !== "all") {
      const p = PERIODS.find((x) => x.key === period);
      const cutoff = Date.now() - p.days * 86400 * 1000;
      list = list.filter((h) => new Date(h.date).getTime() >= cutoff);
    }
    return list.map((h) => ({ ...h, _label: dateFR(h.date) }));
  }, [data, supplierFilter, period]);

  const stats = useMemo(() => {
    if (filteredHistory.length === 0) return null;
    const vals = filteredHistory.map((h) => h._norm).filter((v) => v != null);
    if (vals.length === 0) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const last = filteredHistory[filteredHistory.length - 1];
    const first = filteredHistory[0];
    const evolution = filteredHistory.length >= 2
      ? ((last._norm - first._norm) / first._norm) * 100
      : null;
    const previous = filteredHistory.length >= 2 ? filteredHistory[filteredHistory.length - 2] : null;
    return { min, max, last, previous, evolution, unit: last._unit };
  }, [filteredHistory]);

  useEffect(() => { setChartKey((k) => k + 1); }, [supplierFilter, period, productId]);

  // Auto-élargit la période au chargement si la période courante coupe
  // l'historique à moins de 2 points alors que le produit possède ≥ 2
  // relevés validés (ex. Campari : deux factures espacées de 3+ mois).
  // Le graphique reste ainsi toujours utile à l'ouverture.
  const periodAutoAdjusted = useRef(false);
  useEffect(() => {
    if (periodAutoAdjusted.current) return;
    if (!data?.history) return;
    const fullLen = data.history.length;
    if (fullLen < 2) return;
    const daysOf = (k) => PERIODS.find((p) => p.key === k)?.days ?? null;
    const countIn = (k) => {
      const d = daysOf(k);
      if (d == null) return fullLen;
      const cutoff = Date.now() - d * 86400 * 1000;
      return data.history.filter((h) => new Date(h.date).getTime() >= cutoff).length;
    };
    if (countIn(period) >= 2) { periodAutoAdjusted.current = true; return; }
    for (const p of PERIODS) {
      if (!planAllows(userPlan, p.minPlan)) continue;
      if (countIn(p.key) >= 2) {
        periodAutoAdjusted.current = true;
        setPeriod(p.key);
        return;
      }
    }
    periodAutoAdjusted.current = true;
  }, [data, userPlan, period]);

  const openInvoice = async (invId) => {
    try {
      const inv = await api.getInvoice(invId);
      setInvoicePreview(inv);
    } catch { /* toast handled elsewhere */ }
  };

  const startEditIdentity = () => {
    if (!data?.product) return;
    const p0 = data.product;
    setIdentityDraft({
      brand: p0.brand || "",
      content_value: p0.content_value ?? "",
      content_unit: p0.content_unit || "",
      // Feb 2026 §« catégorie principale modifiable » — champ ajouté
      // avec override humain (category_source="human") pour éviter
      // que l'inférence OCR ré-écrive « Serviettes bordeaux → Vins ».
      category: p0.category || "Divers",
      subcategory: p0.subcategory || "",
      purchase_unit: p0.purchase_unit || "unité",
      pack_units: p0.pack_units ?? 1,
      purchase_quantity: p0.purchase_quantity ?? 1,
      supplier_id: p0.supplier_id || "",
      supplier_ref: p0.supplier_ref || "",
      ean: p0.ean || "",
    });
    setEditIdentity(true);
  };
  const cancelEditIdentity = () => { setEditIdentity(false); setIdentityDraft(null); };
  const saveIdentity = async () => {
    if (!data?.product || !identityDraft) return;
    setSavingIdentity(true);
    try {
      const payload = {
        ...data.product,
        ...identityDraft,
        content_value: identityDraft.content_value === "" ? null : Number(identityDraft.content_value),
        content_unit: identityDraft.content_unit || null,
        pack_units: Number(identityDraft.pack_units) || 1,
        purchase_quantity: Number(identityDraft.purchase_quantity) || 1,
        supplier_id: identityDraft.supplier_id || null,
        // Feb 2026 §« override catégorie humain persistant » — Si la
        // catégorie a changé, marque `category_source="human"` pour que
        // l'inférence OCR future respecte ce choix.
        ...(identityDraft.category && identityDraft.category !== (data.product?.category || "Divers")
          ? { category: identityDraft.category, category_source: "human" }
          : {}),
      };
      delete payload.display_costs;
      await api.updateProduct(productId, payload);
      const fresh = await api.productDetail(productId);
      setData(fresh);
      setEditIdentity(false); setIdentityDraft(null);
      toast.success("Fiche produit mise à jour");
      onChanged?.();
    } catch {
      toast.error("Échec de la mise à jour");
    } finally {
      setSavingIdentity(false);
    }
  };

  if (loading || !data) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <div className="py-8 flex items-center justify-center gap-2 text-[#5E6875]">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement de la fiche…
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const p = data.product;
  const dc = p.display_costs || {};
  const currentNormalized =
    dc.cost_per_L != null ? { v: dc.cost_per_L, u: "€/L" }
    : dc.cost_per_kg != null ? { v: dc.cost_per_kg, u: "€/kg" }
    : dc.cost_per_unit != null ? { v: dc.cost_per_unit, u: "€/u" }
    : null;

  // Feb 2026 — « Prix actuel » vs « Prix de référence ».
  // Pour un produit servi/utilisé ENTIER (canette, burrata, pot, brique…),
  // on affiche le coût d'UNE unité individuelle comme prix principal, et
  // on garde €/L ou €/kg comme prix de référence secondaire.
  //
  //   Canette Slim 33 cl (ref = 1,396 €/L) → 0,461 €/canette · réf 1,396 €/L
  //   Burratina 125 g   (ref = 2,05 €/u)  → 2,05 €/pièce   · réf 16,40 €/kg
  //   Farine 25 kg      (ref = 1,20 €/kg) → 1,20 €/kg (pas de per-piece)
  const PIECE_LABEL_MAP = {
    canette: "canette", bouteille: "bouteille", bocal: "bocal",
    boite: "boîte", "boîte": "boîte", pot: "pot", sachet: "sachet",
    brique: "brique", tube: "tube", barquette: "barquette",
    "unité": "pièce", unite: "pièce", u: "pièce", "pièce": "pièce", piece: "pièce",
  };
  const _cvToBase = (val, unit, base) => {
    const v = Number(val || 0);
    if (!v || !unit) return null;
    const u = String(unit).toLowerCase();
    if (base === "L")  return u === "l" ? v : u === "cl" ? v * 0.01 : u === "ml" ? v * 0.001 : u === "dl" ? v * 0.1 : null;
    if (base === "kg") return u === "kg" ? v : u === "g" ? v * 0.001 : null;
    return null;
  };
  const _cvToKg = (v, u) => _cvToBase(v, u, "kg");
  const _cvToL  = (v, u) => _cvToBase(v, u, "L");

  const refPrice = Number(p.ref_price_per_base ?? p.purchase_price ?? 0);
  const refBase = p.ref_base_unit || p.purchase_unit;
  const purchaseUnitKey = String(p.purchase_unit || "").toLowerCase();
  const pieceLabel = PIECE_LABEL_MAP[purchaseUnitKey] || "pièce";

  let pricePerPiece = null;
  let refSecondary = null; // { value, unit } — €/kg ou €/L à afficher en secondaire
  if (refBase === "u" && refPrice > 0) {
    // Prix déjà par pièce, on dérive €/kg ou €/L si le contenu est connu.
    pricePerPiece = refPrice;
    const kg = _cvToKg(p.content_value, p.content_unit);
    const L  = _cvToL(p.content_value, p.content_unit);
    if (kg && kg > 0)      refSecondary = { value: refPrice / kg, unit: "kg" };
    else if (L && L > 0)   refSecondary = { value: refPrice / L,  unit: "L" };
  } else if ((refBase === "kg" || refBase === "L") && refPrice > 0) {
    const contentInBase = _cvToBase(p.content_value, p.content_unit, refBase);
    const isIndividualContainer = PIECE_LABEL_MAP[purchaseUnitKey] !== undefined;
    const isMultipack = ["pack", "lot", "carton", "caisse"].includes(purchaseUnitKey);
    if (contentInBase && contentInBase > 0 && (isIndividualContainer || isMultipack)) {
      pricePerPiece = refPrice * contentInBase;
      refSecondary = { value: refPrice, unit: refBase };
    }
  }

  return (
    <>
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden bg-white
                   max-w-full w-screen h-[100dvh] max-h-[100dvh] rounded-none border-0
                   sm:max-w-[1100px] sm:w-[95vw] sm:h-auto sm:max-h-[92vh] sm:rounded-lg sm:border
                   flex flex-col
                   [&>button.absolute.right-4.top-4]:hidden"
        data-testid="product-detail-dialog"
      >
        {/* Hero compact — bandeau bleu profond réduit d'environ 40 % pour
            garder la courbe et le contenu visible sans scroll. Sur mobile
            (< 640 px), le bandeau est fixe (barre supérieure toujours visible)
            avec le bouton fermer accessible ; la zone contenu défile
            indépendamment en dessous. */}
        <div
          className="relative bg-[#0B1F33] text-white shrink-0"
          style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        >
          <div className="px-3 sm:px-5 pt-3 pb-3 flex items-center gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center text-white/85 shrink-0"
              data-testid="product-detail-back"
              aria-label="Retour"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <CategoryIcon
              category={p.category}
              size={44}
              rounded="md"
              className="border border-white/15 shrink-0 hidden xs:block sm:block"
            />

            <div className="min-w-0 flex-1">
              <div className="font-heading text-[15px] sm:text-[20px] leading-tight font-extrabold text-white break-words line-clamp-2 sm:truncate" data-testid="product-detail-name">
                {p.name}
              </div>
              <div className="mt-0.5 text-[10px] sm:text-[10.5px] uppercase tracking-widest font-bold text-[#FFD166] truncate" data-testid="product-detail-category">
                {p.category || "Divers"}{p.content_value && p.content_unit ? ` · ${p.content_value} ${p.content_unit}` : ""}
              </div>
              {p.price_suspicious && Array.isArray(p.price_flags) && p.price_flags.length > 0 && (
                <div
                  data-testid="product-detail-price-suspicious"
                  title={p.price_flags.map((f) => f.message).join("\n")}
                  className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-widest bg-[#FFF4D6] text-[#B36B00] border border-[#F1D9A8]"
                >
                  <AlertTriangle className="w-3 h-3" />
                  Prix à vérifier
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6 w-full min-w-0"
          style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom, 0px))" }}
        >
          {/* Identity + KPIs */}
          <div className="grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5 min-w-0">
            <div className="rounded-xl border border-[#CDD7E3] bg-white p-4 sm:p-5 min-w-0" data-testid="product-identity-card">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">Identité produit</div>
                {!editIdentity ? (
                  <button
                    type="button"
                    onClick={startEditIdentity}
                    data-testid="product-identity-edit"
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11.5px] font-semibold text-[#0B1F33] border border-[#CDD7E3] hover:bg-[#F4F5EF]"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Modifier
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={cancelEditIdentity}
                      data-testid="product-identity-cancel"
                      disabled={savingIdentity}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11.5px] font-semibold text-[#5E6875] border border-[#CDD7E3] hover:bg-[#F4F5EF]"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Annuler
                    </button>
                    <button
                      type="button"
                      onClick={saveIdentity}
                      disabled={savingIdentity}
                      data-testid="product-identity-save"
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11.5px] font-bold text-[#0B1F33] bg-[#FF9F1C] hover:bg-[#F58F06]"
                    >
                      {savingIdentity ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Enregistrer
                    </button>
                  </div>
                )}
              </div>
              {!editIdentity ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px] min-w-0 break-words">
                  <KV icon={<Tag className="w-3.5 h-3.5" />} k="Marque" v={p.brand || "—"} />
                  <KV icon={<Package className="w-3.5 h-3.5" />} k="Contenance" v={p.content_value && p.content_unit ? `${p.content_value} ${p.content_unit}` : "—"} />
                  <KV k="Sous-catégorie" v={p.subcategory || "—"} />
                  <KV k="Unité de référence" v={unitLabel(p.ref_base_unit || p.purchase_unit) || "—"} />
                  <KV k="Conditionnement" v={p.pack_units && p.pack_units > 1 ? `${p.pack_units} × ${p.purchase_quantity} ${unitLabel(p.purchase_unit)}` : `${p.purchase_quantity} ${unitLabel(p.purchase_unit)}`} />
                  <KV icon={<Building2 className="w-3.5 h-3.5" />} k="Fournisseur principal" v={data.supplier?.name || "—"} />
                  <KV k="Référence fournisseur" v={p.supplier_ref || "—"} />
                  <KV icon={<Barcode className="w-3.5 h-3.5" />} k="EAN" v={p.ean || "—"} />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px] min-w-0">
                  <EditField label="Marque" testid="pi-brand">
                    <Input value={identityDraft.brand} onChange={(e) => setIdentityDraft({ ...identityDraft, brand: e.target.value })} className="!h-9 text-[12.5px]" />
                  </EditField>
                  <EditField label="Contenance">
                    <div className="flex gap-1.5">
                      <Input type="number" step="0.01" value={identityDraft.content_value} onChange={(e) => setIdentityDraft({ ...identityDraft, content_value: e.target.value })} className="!h-9 text-[12.5px] flex-1" data-testid="pi-content-value" />
                      <Select value={identityDraft.content_unit || "__none"} onValueChange={(v) => setIdentityDraft({ ...identityDraft, content_unit: v === "__none" ? "" : v })}>
                        <SelectTrigger className="!h-9 text-[12.5px] w-[92px]" data-testid="pi-content-unit"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">—</SelectItem>
                          {UNITS.map((u) => <SelectItem key={u} value={u}>{unitLabel(u)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </EditField>
                  <EditField label="Catégorie principale" testid="pi-category">
                    <Select value={identityDraft.category || "Divers"} onValueChange={(v) => setIdentityDraft({ ...identityDraft, category: v })}>
                      <SelectTrigger className="!h-9 text-[12.5px]" data-testid="pi-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Vins","Spiritueux","Boissons","Viandes","Produits de la mer","Crèmerie","Fruits & Légumes","Épicerie","Consommables","Divers"].map((c) =>
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </EditField>
                  <EditField label="Sous-catégorie">
                    <Input value={identityDraft.subcategory} onChange={(e) => setIdentityDraft({ ...identityDraft, subcategory: e.target.value })} className="!h-9 text-[12.5px]" data-testid="pi-subcategory" />
                  </EditField>
                  <EditField label="Unité de référence">
                    <Select value={identityDraft.purchase_unit} onValueChange={(v) => setIdentityDraft({ ...identityDraft, purchase_unit: v })}>
                      <SelectTrigger className="!h-9 text-[12.5px]" data-testid="pi-purchase-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{unitLabel(u)}</SelectItem>)}</SelectContent>
                    </Select>
                  </EditField>
                  <EditField label="Conditionnement">
                    <div className="flex gap-1.5 items-center">
                      <Input type="number" value={identityDraft.pack_units} onChange={(e) => setIdentityDraft({ ...identityDraft, pack_units: e.target.value })} className="!h-9 text-[12.5px] w-[62px]" data-testid="pi-pack-units" />
                      <span className="text-[#5E6875]">×</span>
                      <Input type="number" step="0.01" value={identityDraft.purchase_quantity} onChange={(e) => setIdentityDraft({ ...identityDraft, purchase_quantity: e.target.value })} className="!h-9 text-[12.5px] flex-1" data-testid="pi-purchase-quantity" />
                    </div>
                  </EditField>
                  <EditField label="Fournisseur principal">
                    <Select value={identityDraft.supplier_id || "__none"} onValueChange={(v) => setIdentityDraft({ ...identityDraft, supplier_id: v === "__none" ? "" : v })}>
                      <SelectTrigger className="!h-9 text-[12.5px]" data-testid="pi-supplier"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— Aucun —</SelectItem>
                        {suppliersList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </EditField>
                  <EditField label="Référence fournisseur">
                    <Input value={identityDraft.supplier_ref} onChange={(e) => setIdentityDraft({ ...identityDraft, supplier_ref: e.target.value })} className="!h-9 text-[12.5px]" data-testid="pi-supplier-ref" />
                  </EditField>
                  <EditField label="EAN">
                    <Input value={identityDraft.ean} onChange={(e) => setIdentityDraft({ ...identityDraft, ean: e.target.value })} className="!h-9 text-[12.5px] font-mono-num" data-testid="pi-ean" />
                  </EditField>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[#CDD7E3] bg-white p-4 sm:p-5">
              <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-3">Prix actuel</div>
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[28px] sm:text-[32px] font-heading font-extrabold text-[#0B1F33] tabular-nums leading-none" data-testid="product-detail-price-main">
                    {eur(refPrice, 3)}
                    <span className="text-[13px] text-[#5E6875] ml-1 font-semibold">/ {unitLabel(refBase)}</span>
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest font-bold text-[#5E6875]">Prix de référence</div>
                </div>
                {stats && stats.previous && (
                  <VariationChip
                    fromPrev={((stats.last._norm - stats.previous._norm) / (stats.previous._norm || 1)) * 100}
                    testid="detail-var-prev"
                  />
                )}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-[11.5px]">
                <MetricBox k="Prix min" v={stats ? `${eur(stats.min, 3)} ${stats.unit}` : "—"} />
                <MetricBox k="Prix max" v={stats ? `${eur(stats.max, 3)} ${stats.unit}` : "—"} />
                <MetricBox k="Meilleur fournisseur" v={data.best_supplier?.name || "—"} />
                <MetricBox k="Dernière mise à jour" v={dateFR(p.price_date)} />
              </div>
            </div>
          </div>

          {/* Price evolution chart */}
          <div className="rounded-xl border border-[#CDD7E3] bg-white p-5" data-testid="product-detail-chart">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">Évolution du prix</div>
                <div className="text-[15px] font-heading font-bold text-[#0B1F33]">Historique normalisé{stats ? ` en ${stats.unit}` : ""}</div>
              </div>
              <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto">
                <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                  <SelectTrigger className="h-8 text-[12px] w-full sm:w-[200px]" data-testid="chart-supplier-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les fournisseurs</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex rounded-md border border-[#CDD7E3] overflow-hidden" role="group" aria-label="Période">
                  {PERIODS.map((p) => {
                    const locked = !planAllows(userPlan, p.minPlan);
                    return (
                      <button
                        key={p.key}
                        onClick={() => {
                          if (locked) { toast.info(`Période ${p.label} réservée à la formule ${p.minPlan === "pro" ? "Pro" : "Intégral"}`); return; }
                          setPeriod(p.key);
                        }}
                        className={`px-2.5 h-8 text-[11.5px] font-semibold inline-flex items-center gap-1 ${
                          period === p.key ? "bg-[#0B1F33] text-white"
                          : locked ? "bg-white text-[#8A94A6] hover:bg-[#F4F5EF]"
                          : "bg-white text-[#0B1F33] hover:bg-[#F4F5EF]"
                        }`}
                        data-testid={`chart-period-${p.key}`}
                        aria-pressed={period === p.key}
                        title={locked ? `Passez en ${p.minPlan === "pro" ? "Pro" : "Intégral"} pour débloquer` : undefined}
                      >
                        {locked && <Lock className="w-3 h-3" />}
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                {/* Toggle courbe / barres — persistant en localStorage */}
                <div className="flex rounded-md border border-[#CDD7E3] overflow-hidden" role="group" aria-label="Type de visualisation">
                  <button
                    type="button"
                    onClick={() => setChartMode("line")}
                    className={`px-2 h-8 inline-flex items-center gap-1 text-[11.5px] font-semibold ${chartMode === "line" ? "bg-[#0B1F33] text-white" : "bg-white text-[#0B1F33] hover:bg-[#F4F5EF]"}`}
                    data-testid="chart-mode-line"
                    aria-pressed={chartMode === "line"}
                    title="Courbe"
                  >
                    <LineChartIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setChartMode("bar")}
                    className={`px-2 h-8 inline-flex items-center gap-1 text-[11.5px] font-semibold ${chartMode === "bar" ? "bg-[#0B1F33] text-white" : "bg-white text-[#0B1F33] hover:bg-[#F4F5EF]"}`}
                    data-testid="chart-mode-bar"
                    aria-pressed={chartMode === "bar"}
                    title="Barres"
                  >
                    <BarChart3 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            {filteredHistory.length === 0 ? (
              <div className="py-12 text-center text-[13px] text-[#5E6875]">
                Aucun prix enregistré pour cette période.
              </div>
            ) : (
              <div style={{ height: 240, minHeight: 200 }} className="pd-chart sm:!h-[280px]">
                {/* Local keyframes for the sequential dot pop + variation reveal.
                    prefers-reduced-motion disables the animations. */}
                <style>{`
                  @keyframes pd-dot-pop { from { opacity: 0; transform: scale(0.2); } to { opacity: 1; transform: scale(1); } }
                  @keyframes pd-reveal  { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
                  .pd-chart .pd-dot { opacity: 0; transform-origin: center; transform-box: fill-box; animation: pd-dot-pop 320ms cubic-bezier(.22,.61,.36,1) forwards; }
                  .pd-chart .pd-dot--last { filter: drop-shadow(0 0 6px rgba(255,159,28,0.55)); }
                  .pd-variation { opacity: 0; animation: pd-reveal 500ms cubic-bezier(.22,.61,.36,1) forwards; }
                  @media (prefers-reduced-motion: reduce) {
                    .pd-chart .pd-dot, .pd-variation { animation: none !important; opacity: 1 !important; transform: none !important; }
                  }
                `}</style>
                {chartMode === "bar" ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={240}>
                    <BarChart key={chartKey} data={filteredHistory} margin={{ top: 10, right: 20, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke="#E9EDF3" vertical={false} />
                      <XAxis dataKey="_label" tick={{ fontSize: 11, fill: "#5E6875" }} stroke="#CDD7E3" />
                      <YAxis tick={{ fontSize: 11, fill: "#5E6875" }} stroke="#CDD7E3"
                        tickFormatter={(v) => `${v.toFixed(2)}`} />
                      <RcTooltip content={<ChartTooltip />} cursor={{ fill: "#F4F5EF" }} />
                      <Bar
                        dataKey="_norm"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={44}
                        isAnimationActive={!reducedMotion}
                        animationDuration={reducedMotion ? 0 : 700}
                        animationEasing="ease-out"
                      >
                        {filteredHistory.map((h, idx) => {
                          const isLast = idx === filteredHistory.length - 1;
                          const prev = idx > 0 ? filteredHistory[idx - 1]._norm : null;
                          const up = isLast && prev != null && h._norm > prev;
                          const down = isLast && prev != null && h._norm < prev;
                          const fill = up ? "#D84343" : down ? "#24845B" : "#1769E0";
                          return <Cell key={`bar-${idx}`} fill={fill} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  // Mode courbe — composant custom rendu hors ResponsiveContainer
                  // (recharts casse la mise en page d'un SVG custom).
                  <ProductPriceChart
                    key={chartKey}
                    points={filteredHistory.map((h) => ({
                      label: h._label, value: h._norm,
                      // Feb 2026 §« clic point → édition ligne source » —
                      // Expose l'invoice_id + line_id pour ré-utiliser le
                      // MÊME éditeur que Voir facture (aucune copie locale).
                      invoice_id: h.invoice_id,
                      line_id: h.line_id,
                      price_ht: h.price_ht,
                      date: h.date,
                    }))}
                    heightClassName="h-full"
                    valueSuffix={stats?.unit?.replace("€/", "€ / ") || "€"}
                    testId="pd-price-chart"
                    onPointEdit={async (pt) => {
                      if (!pt?.invoice_id) return;
                      try {
                        const inv = await api.getInvoice(pt.invoice_id);
                        const idx = (inv.lines || []).findIndex(
                          (l) => l.line_id === pt.line_id
                          || (l.matched_product_id === productId && Math.abs(Number(l.line_total_ht) - Number(pt.price_ht)) < 0.01)
                        );
                        if (idx < 0) {
                          toast.error("Ligne source introuvable dans cette facture.");
                          return;
                        }
                        setChartEdit({ invoice: inv, lineIndex: idx, line: inv.lines[idx] });
                      } catch (e) {
                        toast.error("Impossible de charger la facture source.");
                      }
                    }}
                  />
                )}
                {stats && stats.previous && (() => {
                  const varPct = ((stats.last._norm - stats.previous._norm) / (stats.previous._norm || 1)) * 100;
                  const up = varPct > 0.05; const down = varPct < -0.05;
                  const color = up ? "#D84343" : down ? "#24845B" : "#5E6875";
                  const bg = up ? "#FDECEC" : down ? "#E4F1EB" : "#F4F5EF";
                  const delay = reducedMotion ? 0 : 1100;
                  return (
                    <div
                      key={`var-${chartKey}`}
                      className="pd-variation mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-mono-num font-bold text-[12px]"
                      style={{ color, background: bg, animationDelay: `${delay}ms` }}
                      data-testid="chart-variation-reveal"
                    >
                      {up ? <TrendingUp className="w-3.5 h-3.5" /> : down ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                      Évolution sur la période : {varPct > 0 ? "+" : ""}{varPct.toFixed(1)} %
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Prices per invoice list */}
          <div className="rounded-xl border border-[#CDD7E3] bg-white">
            <div className="px-5 py-3 border-b border-[#CDD7E3] flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#1769E0]" />
              <div className="font-heading font-bold text-[15px] text-[#0B1F33]">Factures ({data.linked_invoices.length})</div>
            </div>
            {data.linked_invoices.length === 0 ? (
              <div className="p-6 text-center text-[13px] text-[#5E6875]">Aucune facture enregistrée pour ce produit.</div>
            ) : (
              <div className="divide-y divide-[#CDD7E3]">
                {data.linked_invoices.map((iv) => (
                  <div key={iv.id} className="px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`product-linked-invoice-${iv.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-[#0B1F33] truncate">
                        {iv.supplier_name || "—"} · {iv.invoice_number || "—"}
                      </div>
                      <div className="text-[11.5px] text-[#5E6875]">{dateFR(iv.invoice_date)}</div>
                    </div>
                    <div className="text-right">
                      {/* Feb 2026 §« MEMES BONS PRIX PARTOUT » v3 —
                          SEULE SOURCE : `price_reference` renvoyée par
                          l'API (recalculée depuis `compute_reference_price`
                          sur la ligne complète, reviewed_* inclus).
                          Les `price_per_l/kg/unit` sont des VALEURS
                          SECONDAIRES informatives (une canette a un
                          `price_per_l` mais sa vraie base est €/pièce).
                          Zéro division côté front. Fallback affiche
                          « — » lisiblement, jamais un prix brut de colis. */}
                      {(() => {
                        const pr = iv.price_reference || {};
                        const pb = Number(pr.price_per_base) || 0;
                        const bu = pr.base_unit;
                        if (pb > 0 && bu) {
                          const lbl = bu === "u" ? "pièce" : bu;
                          return (
                            <div className="text-[13px] font-mono-num font-bold text-[#0B1F33]">
                              {eur(pb, 3)} <span className="text-[#5E6875] font-normal text-[11px]">/ {lbl}</span>
                            </div>
                          );
                        }
                        return (
                          <div className="text-[13px] font-mono-num font-bold text-[#5E6875]">— <span className="text-[11px]">à vérifier</span></div>
                        );
                      })()}
                      <div className="text-[11px] font-mono-num text-[#5E6875]">
                        {eur(iv.line_total_ht)} total
                      </div>
                    </div>
                    {iv.file_url && !(String(iv.id || "").startsWith("manual:")) && (
                      <button onClick={() => openInvoice(iv.id)}
                        data-testid={`product-view-invoice-${iv.id}`}
                        className="h-8 px-3 rounded-md border border-[#0B1F33] bg-white hover:bg-[#F4F5EF] text-[12px] font-semibold text-[#0B1F33] inline-flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" /> Voir la facture
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked recipes — chaque ligne est un vrai lien vers la fiche recette,
              avec produit responsable, évolution du coût et rentabilité. */}
          <div className="rounded-xl border border-[#CDD7E3] bg-white">
            <div className="px-5 py-3 border-b border-[#CDD7E3] flex items-center gap-2">
              <ChefHat className="w-4 h-4 text-[#1769E0]" />
              <div className="font-heading font-bold text-[15px] text-[#0B1F33]">
                Recettes liées ({data.linked_recipes.length})
                {data.linked_recipes.some((r) => r.under_target) && (
                  <span className="ml-2 text-[12px] font-semibold text-[#D84343]" data-testid="product-linked-under-count">
                    · {data.linked_recipes.filter((r) => r.under_target).length} à optimiser
                  </span>
                )}
              </div>
            </div>
            {data.linked_recipes.length === 0 ? (
              <div className="p-6 text-center text-[13px] text-[#5E6875]">Aucune fiche recette n'utilise ce produit.</div>
            ) : (
              <ul className="divide-y divide-[#CDD7E3]">
                {data.linked_recipes.map((r) => {
                  const under = r.under_target;
                  const delta = r.cost_delta_per_portion;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => setOverlayRecipeId(r.id)}
                        data-testid={`product-linked-recipe-${r.id}`}
                        className="w-full text-left px-4 py-3 flex items-center gap-3 sm:gap-4 hover:bg-[#F4F5EF] active:bg-[#E9EDF3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9F1C] focus-visible:ring-inset transition-colors cursor-pointer"
                      >
                        <SafeImage src={r.photo_url} alt={r.name} className="w-14 h-14 rounded-md shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[14px] font-semibold text-[#0B1F33] truncate">{r.name}</div>
                          <div className="text-[11px] text-[#5E6875] mt-0.5 truncate">
                            {/* Feb 2026 §« Produit responsable » — n'affiche
                                cette étiquette QUE si ce produit contribue
                                réellement à la variation du coût recette.
                                Un produit stable (delta ≈ 0) reste lié à la
                                recette mais n'en est pas responsable de la
                                hausse — souvent un autre ingrédient l'est. */}
                            {delta != null && Math.abs(delta) >= 0.001 ? (
                              <>Produit responsable · <span className="font-semibold text-[#0B1F33]">{p.name}</span></>
                            ) : (
                              <>Recette liée</>
                            )}
                            {r.quantity != null && r.unit ? <> · {r.quantity} {r.unit}</> : null}
                          </div>
                        </div>
                        <div className="hidden sm:flex flex-col items-end shrink-0 gap-1">
                          {/* Feb 2026 §« 0.000 € / portion trompeur » —
                              Si `delta` est nul (produit stable), afficher
                              « Impact nul » plutôt qu'un montant à 3 décimales
                              qui laisse croire à un chiffrage précis. */}
                          <span className="text-[11px] text-[#5E6875] tabular-nums" data-testid={`product-linked-cost-${r.id}`}>
                            Coût / portion <span className="font-bold text-[#0B1F33]">{eur(r.matter_cost_per_portion, 3)}</span>
                          </span>
                          <span className={`text-[11px] font-bold tabular-nums ${under ? "text-[#D84343]" : "text-[#24845B]"}`}>
                            Marge {r.matter_margin_pct != null ? `${r.matter_margin_pct.toFixed(1)} %` : "—"}
                            {r.target_margin_pct != null && <span className="text-[#5E6875] font-normal"> · obj. {r.target_margin_pct}%</span>}
                          </span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#5E6875] shrink-0" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Aliases */}
          <div className="rounded-xl border border-[#CDD7E3] bg-white" data-testid="product-aliases-section">
            <div className="px-5 py-3 border-b border-[#CDD7E3] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#1769E0]" />
              <div className="font-heading font-bold text-[15px] text-[#0B1F33]">Écritures reconnues sur factures ({aliases.length})</div>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-[12px] text-[#5E6875]">
                Chaque écriture ci-dessous a été reliée à ce produit sur au moins une facture.
                Toutes désignent le <b>même produit canonique</b>.
              </p>
              {aliases.length === 0 ? (
                <div className="text-[13px] text-[#5E6875] italic py-2">Aucun alias enregistré pour ce produit.</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {aliases.map((a) => (
                    <div key={`${a.supplier_key}|${a.raw_key}`} className="rounded-md border border-[#CDD7E3] bg-[#F4F5EF] p-2 flex items-center gap-2" data-testid={`alias-row-${a.raw_key}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-mono-num font-bold text-[#0B1F33] truncate">« {a.raw_key} »</div>
                        <div className="text-[11px] text-[#5E6875] truncate">
                          {a.supplier_key ? `Fournisseur : ${a.supplier_key}` : "Tous fournisseurs"}
                          {a.note ? ` · ${a.note}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Retirer l'alias « ${a.raw_key} » ?`)) return;
                          try {
                            await api.deleteProductAlias(a.supplier_key || "", a.raw_key);
                            reloadAliases();
                            toast.success("Alias supprimé");
                          } catch { toast.error("Échec suppression"); }
                        }}
                        data-testid={`alias-delete-${a.raw_key}`}
                        className="w-7 h-7 rounded hover:bg-[#FDECEC] text-[#D84343] flex items-center justify-center"
                        title="Retirer cet alias"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="pt-2 border-t border-dashed border-[#CDD7E3] grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                <Input
                  placeholder="Écriture facture (ex: RCRD 70)"
                  value={newAlias.raw_text}
                  onChange={(e) => setNewAlias({ ...newAlias, raw_text: e.target.value })}
                  data-testid="alias-input-raw"
                  className="!h-9 text-[12.5px] font-mono-num"
                />
                <Input
                  placeholder="Fournisseur (facultatif)"
                  value={newAlias.supplier_name}
                  onChange={(e) => setNewAlias({ ...newAlias, supplier_name: e.target.value })}
                  data-testid="alias-input-supplier"
                  className="!h-9 text-[12.5px]"
                />
                <Button
                  size="sm"
                  onClick={async () => {
                    if (!newAlias.raw_text.trim()) { toast.error("Écriture requise"); return; }
                    try {
                      await api.addProductAlias({ ...newAlias, product_id: productId });
                      setNewAlias({ raw_text: "", supplier_name: "" });
                      reloadAliases();
                      toast.success("Alias ajouté");
                    } catch { toast.error("Échec ajout alias"); }
                  }}
                  data-testid="alias-add-btn"
                  className="!h-9 bg-[#0B1F33] hover:bg-[#1a3050] text-white gap-1"
                >
                  <Plus className="w-3.5 h-3.5" /> Ajouter
                </Button>
              </div>
            </div>
          </div>

          {/* Merge into another canonical product */}
          <div className="rounded-xl border border-[#CDD7E3] bg-white" data-testid="product-merge-section">
            <div className="px-5 py-3 border-b border-[#CDD7E3] flex items-center gap-2">
              <GitMerge className="w-4 h-4 text-[#1769E0]" />
              <div className="font-heading font-bold text-[15px] text-[#0B1F33]">Fusionner dans un autre produit</div>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <Select value={mergeInto} onValueChange={setMergeInto}>
                <SelectTrigger className="!h-9 text-[12.5px]" data-testid="merge-target-select">
                  <SelectValue placeholder="Sélectionner un produit cible…" />
                </SelectTrigger>
                <SelectContent>
                  {allProducts.filter((p) => p.id !== productId).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.brand ? ` · ${p.brand}` : ""}{p.content_value && p.content_unit ? ` · ${p.content_value} ${p.content_unit}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={async () => {
                  if (!mergeInto) { toast.error("Sélectionnez un produit cible"); return; }
                  if (!window.confirm("Fusionner ce produit dans la cible ? Toutes les factures, alias, plats et historique seront rattachés à la cible et ce produit sera supprimé.")) return;
                  try {
                    await api.mergeProduct(productId, mergeInto);
                    toast.success("Fusion effectuée");
                    onChanged?.();
                    onClose?.();
                  } catch { toast.error("Échec de la fusion"); }
                }}
                data-testid="merge-execute-btn"
                className="!h-9 bg-[#FF9F1C] hover:bg-[#F58F06] text-[#0B1F33] font-bold gap-1"
              >
                <GitMerge className="w-3.5 h-3.5" /> Fusionner
              </Button>
            </div>
            <p className="px-4 pb-3 text-[11.5px] text-[#5E6875]">
              La fusion conserve l'historique complet, les alias et les plats. Enregistrée dans l'audit ({" "}
              <span className="font-mono">product_merges</span>{" "}) pour un éventuel retour arrière manuel.
            </p>
          </div>

          <div className="text-[11.5px] text-[#5E6875] flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" />
            Formule utilisée : Variation en % = ((nouveau prix − ancien prix) / ancien prix) × 100.
          </div>
        </div>

        {/* Sub-dialog to open a linked invoice inline — Feb 2026 §« Voir
            facture depuis fiche produit = même dialog éditable » —
            L'onglet Analyse contient les mêmes boutons Modifier par ligne
            que depuis la page Factures (PATCH direct sans changement de
            statut). Aucun autre PDFDialog en lecture seule. */}
        <Dialog open={!!invoicePreview} onOpenChange={(v) => !v && setInvoicePreview(null)}>
          <DialogContent className="p-0 gap-0 overflow-hidden bg-white
                                    max-w-full w-screen h-[100dvh] max-h-[100dvh] rounded-none border-0
                                    sm:max-w-[1200px] sm:w-[95vw] sm:h-auto sm:max-h-[92vh] sm:rounded-lg sm:border
                                    flex flex-col" data-testid="product-invoice-dialog">
            <DialogHeader className="px-5 py-3 border-b border-[#CDD7E3] bg-[#F4F5EF]">
              <DialogTitle className="text-[14px] flex items-center gap-2 min-w-0 pr-8">
                <FileText className="w-4 h-4 text-[#1769E0] shrink-0" />
                <span className="truncate">
                  {invoicePreview?.supplier_name || "—"} · {invoicePreview?.invoice_number || "—"}
                </span>
                <span className="text-[#5E6875] font-normal ml-2 shrink-0 hidden sm:inline">{dateFR(invoicePreview?.invoice_date)}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-0 overflow-hidden">
              <div className="border-r border-[#CDD7E3] min-h-0 overflow-hidden">
                {invoicePreview?.file_url ? (
                  <DocumentViewer src={invoicePreview.file_url} mime={invoicePreview.file_mime} name={invoicePreview.file_name || "Facture"} />
                ) : (
                  <div className="p-8 text-center text-[13px] text-[#5E6875]">Aucun document original disponible.</div>
                )}
              </div>
              <div className="min-h-0 overflow-y-auto">
                <InvoiceAnalysis
                  invoice={invoicePreview}
                  products={allProducts}
                  onLineSaved={async () => {
                    try {
                      const fresh = await api.getInvoice(invoicePreview.id);
                      if (fresh) setInvoicePreview(fresh);
                      const freshDetail = await api.productDetail(productId);
                      setData(freshDetail);
                    } catch { /* silent */ }
                    onChanged?.();
                  }}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
    {/* Feb 2026 — Recette liée ouverte en overlay au-dessus de la fiche
        produit. Aucune navigation vers /plats : à la fermeture, l'utilisateur
        retrouve la fiche produit et sa page d'origine intactes (filtres,
        recherche, défilement, saisie en cours). */}
    <RecipeEditorDialog
      open={!!overlayRecipeId}
      onOpenChange={(v) => { if (!v) setOverlayRecipeId(null); }}
      recipeId={overlayRecipeId}
      onSaved={async () => {
        try { setData(await api.productDetail(productId)); } catch { /* silent */ }
        onChanged?.();
      }}
    />
    {/* Feb 2026 §« clic point courbe → édition ligne source facture » —
        Overlay au-dessus de la fiche produit (idem RecipeEditorDialog).
        Réutilise EXACTEMENT le même InvoiceLineEditDialog que Voir facture. */}
    <InvoiceLineEditDialog
      open={!!chartEdit}
      onOpenChange={(v) => { if (!v) setChartEdit(null); }}
      invoiceId={chartEdit?.invoice?.id}
      lineIndex={chartEdit?.lineIndex}
      initialLine={chartEdit?.line}
      products={allProducts}
      onSaved={async () => {
        try {
          const fresh = await api.productDetail(productId);
          setData(fresh);
        } catch { /* silent */ }
        onChanged?.();
        try { window.dispatchEvent(new CustomEvent("rentascan:invoices-changed", { detail: { source: "chart-edit" } })); } catch {}
      }}
    />
    </>
  );
}

/* ---------- helpers ---------- */
/** Dot that "pops in" once the line has swept past it. */
function AnimatedDot({ cx, cy, index, total, reduced }) {
  if (cx == null || cy == null) return null;
  const isLast = index === total - 1;
  // Line animation is 1000ms; stagger dots to land as the line reaches them.
  const delay = reduced ? 0 : Math.round(200 + (index / Math.max(1, total - 1)) * 800);
  return (
    <circle
      cx={cx}
      cy={cy}
      r={isLast ? 5.5 : 4}
      fill="#FF9F1C"
      stroke="#0B1F33"
      strokeWidth={isLast ? 2 : 1.5}
      className={`pd-dot ${isLast ? "pd-dot--last" : ""}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`chart-dot-${index}`}
    />
  );
}

function KV({ k, v, icon }) {
  return (
    <div className="flex items-center gap-2">
      {icon && <span className="text-[#5E6875]">{icon}</span>}
      <span className="text-[11.5px] text-[#5E6875]">{k}</span>
      <span className="ml-auto text-[12.5px] font-semibold text-[#0B1F33]">{v}</span>
    </div>
  );
}
function EditField({ label, children, testid }) {
  return (
    <label className="flex flex-col gap-1" data-testid={testid}>
      <span className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">{label}</span>
      {children}
    </label>
  );
}
function MetricBox({ k, v }) {
  return (
    <div className="rounded-md border border-[#CDD7E3] bg-[#F4F5EF] p-2">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#5E6875]">{k}</div>
      <div className="text-[12.5px] font-semibold text-[#0B1F33] truncate">{v}</div>
    </div>
  );
}
function VariationChip({ fromPrev, testid }) {
  if (fromPrev == null || !isFinite(fromPrev)) return null;
  const up = fromPrev > 0.05;
  const down = fromPrev < -0.05;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const color = up ? "#D84343" : down ? "#24845B" : "#5E6875";
  const bg = up ? "#FDECEC" : down ? "#E4F1EB" : "#F4F5EF";
  return (
    <div className="text-right" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#5E6875]">vs précédent</div>
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ color, background: bg }}>
        <Icon className="w-3.5 h-3.5" />
        <span className="font-mono-num font-bold text-[12px]">{fromPrev > 0 ? "+" : ""}{fromPrev.toFixed(1)}%</span>
      </div>
    </div>
  );
}
function ChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-[#CDD7E3] bg-white p-2.5 shadow-lg text-[11.5px]" data-testid="chart-tooltip">
      <div className="font-semibold text-[#0B1F33]">{dateFR(p.date)}</div>
      {p.supplier_name && <div className="text-[#5E6875]">{p.supplier_name}</div>}
      <div className="font-mono-num text-[#0B1F33] mt-1">{eur(p._norm, 4)} {p._unit}</div>
      {p.content_value && p.content_unit && <div className="text-[#5E6875]">Conditionnement : {p.content_value} {p.content_unit}</div>}
      {p.variation_pct != null && (
        <div className={`font-mono-num mt-1 font-semibold ${p.variation_pct > 0 ? "text-[#D84343]" : p.variation_pct < 0 ? "text-[#24845B]" : "text-[#5E6875]"}`}>
          {p.variation_pct > 0 ? "+" : ""}{p.variation_pct}%
        </div>
      )}
    </div>
  );
}
